import { ROLE_OPTIONS } from '../../../security'
import { isQcsBotPayload } from '../../../constants/qcs'
import {
  getOrgPackageName,
  getOrgPackageTierLevel,
  cacheBotPackageAssignment,
  getCachedBotPackageAssignment,
} from './orgPackages'
import { supervisionConfigToMode, resolveQcsLandingTab } from '../api/usageLimits'

/** Prefer a human-readable model name; never return a nested config object. */
const resolveAiModelName = bot => {
  if (typeof bot?.aiModel === 'string' && bot.aiModel.trim()) return bot.aiModel.trim()

  const nested = bot?.odinConfigs?.aiModel
  if (typeof nested === 'string' && nested.trim()) return nested.trim()
  if (nested && typeof nested.modelName === 'string' && nested.modelName.trim()) {
    return nested.modelName.trim()
  }
  return null
}

/**
 * Turns a bot payload into the row an operator actually reads.
 *
 * Every value here comes from a real field — `GET /bot` returns `name`,
 * `nlpEnabled`, `contextualizerEnabled`, `updatedAt` and `timezone`, and the
 * signed-in admin's own role per organisation comes from their access record.
 * Nothing is inferred or filled in with a stand-in, because a column an
 * operator cannot trust is worse than no column at all.
 */

export const getInitials = name => {
  if (!name) return '?'
  const words = String(name).trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

export const getRoleLabel = role =>
  ROLE_OPTIONS.find(option => option.value === role)?.label || role

/** An assistant counts as set up once natural-language handling is enabled. */
export const isAssistantReady = bot => Boolean(bot?.nlpEnabled)

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Recency reads faster than a date when scanning a long list: "3 days ago"
 * answers "is this organisation live?" without the reader doing date maths.
 * Older entries fall back to an absolute date, where the exact day matters more
 * than the interval.
 */
export const formatUpdated = updatedAt => {
  if (!updatedAt) return '—'

  const date = new Date(updatedAt)
  if (isNaN(date.getTime())) return '—'

  const days = Math.floor((Date.now() - date.getTime()) / DAY_MS)

  if (days < 0) return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${days} days ago`

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * How urgently a renewal deserves attention — drives the colour of the
 * "N days left" text. The renewal cycle is a month, so the thresholds scale
 * to that: more than half the cycle left reads as fine, the back half is a
 * heads-up, and the final week is urgent.
 */
const getRenewalUrgency = daysLeft => {
  if (daysLeft <= 7) return 'urgent'
  if (daysLeft <= 14) return 'warn'
  return 'ok'
}

/**
 * Renewal from package assignment `renewalCycle: { daysLeft, renewsOn } | null`.
 * Uses API daysLeft when provided; does not invent a date when cycle is null.
 */
export const getRenewalInfoFromCycle = renewalCycle => {
  if (!renewalCycle || renewalCycle.renewsOn == null) return null

  const daysLeft =
    typeof renewalCycle.daysLeft === 'number' && Number.isFinite(renewalCycle.daysLeft)
      ? Math.max(0, Math.floor(renewalCycle.daysLeft))
      : null

  if (daysLeft == null) {
    // Fall back to date-only path if daysLeft missing but renewsOn present.
    return getRenewalInfo(renewalCycle.renewsOn)
  }

  const date = new Date(renewalCycle.renewsOn)
  const dateLabel = !isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  return {
    daysLeft,
    daysLeftLabel: daysLeft <= 0 ? 'Renews today' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`,
    renewsOnLabel: dateLabel ? `Renews on ${dateLabel}` : '—',
    urgency: getRenewalUrgency(daysLeft),
  }
}

/**
 * The renewal cycle countdown from a single renews-on date.
 * Returns `null` when missing — an operator should see "—", never a guessed countdown.
 */
export const getRenewalInfo = renewalDate => {
  if (!renewalDate) return null

  const date = new Date(renewalDate)
  if (isNaN(date.getTime())) return null

  const daysLeft = Math.max(0, Math.ceil((date.getTime() - Date.now()) / DAY_MS))
  const dateLabel = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

  return {
    daysLeft,
    daysLeftLabel: daysLeft <= 0 ? 'Renews today' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`,
    renewsOnLabel: `Renews on ${dateLabel}`,
    urgency: getRenewalUrgency(daysLeft),
  }
}

export const toOrganisationRow = (bot, role) => {
  // Prefer list embed so packageName / packageTier resolve without waiting on N+1.
  if (bot && Object.prototype.hasOwnProperty.call(bot, 'packageDetails')) {
    cacheBotPackageAssignment(bot.id, bot.packageDetails)
  }

  const assignment =
    bot?.packageDetails || getCachedBotPackageAssignment(bot?.id) || null

  return {
    id: bot.id,
    bot,
    name: bot.name || 'Untitled organisation',
    initials: getInitials(bot.name),
    role: role || '',
    assistantReady: isAssistantReady(bot),
    updatedAt: bot.updatedAt || null,
    updatedLabel: formatUpdated(bot.updatedAt),
    isQcs: isQcsBotPayload(bot),
    // First tab this role sees in the QCS rail (Dashboard for Admin / Careco
    // Admin, Professionals for Care Manager). CareBrain always opens Dashboard.
    landingTab: isQcsBotPayload(bot) ? resolveQcsLandingTab(role, bot) : 'dashboard',
    // QCS-only account fields. Absent on a CareBrain bot payload, and on a QCS
    // one until the backend sets them — the column reads that as "unknown",
    // never as a guessed default.
    tier: bot.tier || null,
    // The package assigned to this QCS org — prefer bot/list `packageDetails`,
    // else the assignment cache (GET /bot/{id}/package).
    packageName: bot.packageDetails?.packageName || getOrgPackageName(bot.id),
    // Active mode from assignment supervision DTO when assigned; otherwise bot field.
    // Do not fall back to bot.supervisionMode when the assignment explicitly has
    // both supervision flags off — that must read as disabled (null → "—" in UI).
    supervisionMode: assignment
      ? supervisionConfigToMode(assignment.supervision)
      : bot.supervisionMode || null,
    // AI model applies to both CareBrain and QCS organisations. List payloads
    // usually nest it under odinConfigs — only ever expose a display string so
    // the table/grid never try to render an `{ id, modelName }` object.
    aiModel: resolveAiModelName(bot),
    // Basic / Gold / Platinum from the assigned package's feature count (local).
    packageTier: getOrgPackageTierLevel(bot.id),
    // From list/get bot `botActivated` (always included for QCS APIs).
    botActivated: Boolean(bot.botActivated),
    // From packageDetails.renewalCycle { daysLeft, renewsOn } | null.
    renewalInfo: getRenewalInfoFromCycle(assignment?.renewalCycle),
    // Server-computed care-plan audit usage % from bot/list (QCS). Null when
    // absent so the table/grid fall back to used/limit ratio.
    carePlanAuditUsagePercentage:
      typeof bot.carePlanAuditUsagePercentage === 'number'
        ? bot.carePlanAuditUsagePercentage
        : null,
  }
}
